/**
 * API のエラー封筒（ARCHITECTURE.md §4.3 が唯一の正）。
 *
 *   { "error": { "code": "SNAKE_UPPER", "message": "日本語", "details"?: unknown } }
 *
 * `code` は SPA が分岐に使う安定した機械可読値、`message` はそのまま画面に
 * 出せる日本語。両方を必ず返すことで、SPA 側でメッセージを再実装せずに済む。
 * HTTP ステータスとの対応もここに集約し、API 側で散らばらせない。
 */
import { z } from 'zod';

/** エラーコード。SNAKE_UPPER。**新設するときは必ずここに追加する**。 */
export const ERROR_CODE = {
  /* --- 入力・認証・認可 --- */
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  UNAUTHORIZED: 'UNAUTHORIZED',
  FORBIDDEN: 'FORBIDDEN',
  NOT_FOUND: 'NOT_FOUND',
  CONFLICT: 'CONFLICT',
  /** 楽観ロック／同時更新の衝突。クライアントは再取得してやり直す。 */
  REVISION_CONFLICT: 'REVISION_CONFLICT',
  RATE_LIMITED: 'RATE_LIMITED',
  PAYLOAD_TOO_LARGE: 'PAYLOAD_TOO_LARGE',

  /* --- 認証フロー（§7.1） --- */
  /** MFA チケットが無効・期限切れ・IP/UA 不一致。ログインからやり直す。 */
  MFA_TICKET_INVALID: 'MFA_TICKET_INVALID',
  MFA_REQUIRED: 'MFA_REQUIRED',
  /** `requireFreshAuth(300)` の対象操作で再認証が必要。 */
  REAUTH_REQUIRED: 'REAUTH_REQUIRED',
  /** refresh トークンの再利用を検知。family 全体を失効済み。 */
  SESSION_REVOKED: 'SESSION_REVOKED',
  ACCOUNT_LOCKED: 'ACCOUNT_LOCKED',

  /* --- 業務ルール --- */
  /** details: { limit: string; current: number; max: number; resource: string } */
  PLAN_LIMIT_EXCEEDED: 'PLAN_LIMIT_EXCEEDED',
  /** DNS 検証が済むまで送信を許可しない（§7.4）。 */
  DNS_NOT_VERIFIED: 'DNS_NOT_VERIFIED',
  DOMAIN_ALREADY_CLAIMED: 'DOMAIN_ALREADY_CLAIMED',
  QUOTA_EXCEEDED: 'QUOTA_EXCEEDED',
  ORGANIZATION_SUSPENDED: 'ORGANIZATION_SUSPENDED',
  BILLING_REQUIRED: 'BILLING_REQUIRED',

  /* --- ノード・エージェント --- */
  NODE_UNREACHABLE: 'NODE_UNREACHABLE',
  /** CP 署名の検証失敗、または private_key_jwt の署名不正。 */
  AGENT_SIGNATURE_INVALID: 'AGENT_SIGNATURE_INVALID',
  /** enroll トークンが未発行・使用済み・期限切れ。 */
  ENROLLMENT_TOKEN_INVALID: 'ENROLLMENT_TOKEN_INVALID',
  /** private_key_jwt の `jti` が再利用された。 */
  AGENT_ASSERTION_REPLAYED: 'AGENT_ASSERTION_REPLAYED',
  /** desired-state の世代が巻き戻っている（適用してはならない）。 */
  GENERATION_ROLLBACK: 'GENERATION_ROLLBACK',

  /* --- 配信コンテンツ CMS（CMS_DESIGN.md §5.3） --- */
  /** 楽観ロックの衝突。3-way マージは作らないので、分岐保存か相手の版を開くかを選ばせる。 */
  CONTENT_REVISION_CONFLICT: 'CONTENT_REVISION_CONFLICT',
  /** 他の契約者が編集中（ソフトロック）。 */
  CONTENT_LOCKED: 'CONTENT_LOCKED',
  /** 承認が必要な組織で、未承認のリビジョンを配信に使おうとした。 */
  CONTENT_NOT_APPROVED: 'CONTENT_NOT_APPROVED',
  /** 生成 HTML が 98KB を超えた。Gmail のクリップで法定フッタが見えなくなる。 */
  CONTENT_TOO_LARGE: 'CONTENT_TOO_LARGE',
  CONTENT_LEGAL_FOOTER_MISSING: 'CONTENT_LEGAL_FOOTER_MISSING',
  /** 宛先リストで宣言されていない差し込みキーを使っている。 */
  CONTENT_MERGE_KEY_UNKNOWN: 'CONTENT_MERGE_KEY_UNKNOWN',
  FORM_CHALLENGE_REQUIRED: 'FORM_CHALLENGE_REQUIRED',
  FORM_RATE_LIMITED: 'FORM_RATE_LIMITED',

  /* --- 統合面 / 外部連携（CMS_GAP_AND_API.md §6.4） --- */
  /** 統合面を JWT または無認証で叩いた。**JWT が有効でも通さない**。 */
  INTEGRATION_TOKEN_REQUIRED: 'INTEGRATION_TOKEN_REQUIRED',
  /** 同一 `Idempotency-Key` で異なるボディが来た。 */
  IDEMPOTENCY_KEY_REUSED: 'IDEMPOTENCY_KEY_REUSED',
  /** commit 時に S3 の実体が申告した sha256 / byte_size と違う。 */
  CONTENT_HASH_MISMATCH: 'CONTENT_HASH_MISMATCH',
  /** 未 commit の revision に他の操作をした。 */
  CONTENT_UPLOAD_PENDING: 'CONTENT_UPLOAD_PENDING',
  /** `mutable=false` のアセットを差し替えようとした。 */
  ASSET_IMMUTABLE: 'ASSET_IMMUTABLE',
  TAG_MERGE_TARGET_INVALID: 'TAG_MERGE_TARGET_INVALID',
  /** SSRF ガードに掛かった URL（内部レンジ・非 https・リダイレクト）。 */
  WEBHOOK_URL_BLOCKED: 'WEBHOOK_URL_BLOCKED',
  /** `pii_ack_at` なしで `contact.*` / `suppression.*` を購読しようとした。 */
  WEBHOOK_PII_ACK_REQUIRED: 'WEBHOOK_PII_ACK_REQUIRED',

  /* --- 大量配信（PRODUCT_DIFFERENTIATION.md §5.3 / CMS_DESIGN.md §5.2） --- */
  /** `sending_streams.status` が `verified` でない。DNS と bounce 経路の確認が先。 */
  BULK_NOT_VERIFIED: 'BULK_NOT_VERIFIED',
  /** F5 プリフライトの必須項目が欠けている。**送信を通してはいけない**種類の不備。 */
  BULK_COMPLIANCE_BLOCKED: 'BULK_COMPLIANCE_BLOCKED',
  /** 宛先がサプレッションに載っている（テスト送信・単発送信で返る）。 */
  BULK_SUPPRESSED: 'BULK_SUPPRESSED',
  /** 運営の kill-switch が入っている。契約者側では解除できない。 */
  BULK_KILL_SWITCH_ENGAGED: 'BULK_KILL_SWITCH_ENGAGED',
  /**
   * `awaiting_release` のまま配信を開始しようとした。
   * **統合面（API トークン）からは解禁できない**ことを封筒で明示する。
   */
  CAMPAIGN_RELEASE_REQUIRED: 'CAMPAIGN_RELEASE_REQUIRED',
  /** 未公開・停止中・アーカイブ済みの申込フォームへ submit した。 */
  SIGNUP_FORM_NOT_PUBLISHED: 'SIGNUP_FORM_NOT_PUBLISHED',
  /** 同意証跡（`optin_records`）なしで宛先を購読状態にしようとした。 */
  OPTIN_EVIDENCE_REQUIRED: 'OPTIN_EVIDENCE_REQUIRED',
  /** 本文の閲覧には `break_glass_grants` の有効な付与が要る（C17）。 */
  BREAK_GLASS_REQUIRED: 'BREAK_GLASS_REQUIRED',

  /* --- 法定文書と同意（R0 §2.1） --- */
  /** 最新版の利用規約 / プライバシーポリシーへの同意が無い。 */
  CONSENT_REQUIRED: 'CONSENT_REQUIRED',
  /** 同意しようとした版が現行版でない（表示中に改定された）。読み直させる。 */
  CONSENT_VERSION_STALE: 'CONSENT_VERSION_STALE',
  /** 法定ページが未整備などの理由で申込を受け付けられない。 */
  SIGNUP_UNAVAILABLE: 'SIGNUP_UNAVAILABLE',
  /** 使い捨てメールアドレスなど、申込に使えないメールアドレス。 */
  SIGNUP_EMAIL_REJECTED: 'SIGNUP_EMAIL_REJECTED',

  /* --- 外部・内部 --- */
  UPSTREAM_ERROR: 'UPSTREAM_ERROR',
  SERVICE_UNAVAILABLE: 'SERVICE_UNAVAILABLE',
  INTERNAL_ERROR: 'INTERNAL_ERROR',
  NOT_IMPLEMENTED: 'NOT_IMPLEMENTED',
} as const;

export type ErrorCode = (typeof ERROR_CODE)[keyof typeof ERROR_CODE];

export const ERROR_CODES = Object.values(ERROR_CODE) as readonly ErrorCode[];

/** コード → 既定 HTTP ステータス。ハンドラ側で個別に上書きしてもよい。 */
export const ERROR_STATUS: Readonly<Record<ErrorCode, number>> = {
  VALIDATION_ERROR: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  CONFLICT: 409,
  REVISION_CONFLICT: 409,
  RATE_LIMITED: 429,
  PAYLOAD_TOO_LARGE: 413,

  MFA_TICKET_INVALID: 401,
  MFA_REQUIRED: 401,
  REAUTH_REQUIRED: 401,
  SESSION_REVOKED: 401,
  ACCOUNT_LOCKED: 423,

  // 「上限に達している」は 409（§4.3）。422 ではない。
  PLAN_LIMIT_EXCEEDED: 409,
  DNS_NOT_VERIFIED: 409,
  DOMAIN_ALREADY_CLAIMED: 409,
  QUOTA_EXCEEDED: 409,
  ORGANIZATION_SUSPENDED: 403,
  BILLING_REQUIRED: 402,

  NODE_UNREACHABLE: 503,
  AGENT_SIGNATURE_INVALID: 401,
  ENROLLMENT_TOKEN_INVALID: 401,
  AGENT_ASSERTION_REPLAYED: 401,
  GENERATION_ROLLBACK: 409,

  CONTENT_REVISION_CONFLICT: 409,
  CONTENT_LOCKED: 409,
  CONTENT_NOT_APPROVED: 409,
  CONTENT_TOO_LARGE: 422,
  CONTENT_LEGAL_FOOTER_MISSING: 422,
  CONTENT_MERGE_KEY_UNKNOWN: 422,
  FORM_CHALLENGE_REQUIRED: 429,
  FORM_RATE_LIMITED: 429,

  INTEGRATION_TOKEN_REQUIRED: 401,
  IDEMPOTENCY_KEY_REUSED: 409,
  CONTENT_HASH_MISMATCH: 409,
  CONTENT_UPLOAD_PENDING: 409,
  ASSET_IMMUTABLE: 409,
  TAG_MERGE_TARGET_INVALID: 422,
  WEBHOOK_URL_BLOCKED: 422,
  WEBHOOK_PII_ACK_REQUIRED: 403,

  BULK_NOT_VERIFIED: 409,
  // 「法令上の必須項目が無い」は入力の意味的な誤りなので 422（§4.3 の区分）。
  BULK_COMPLIANCE_BLOCKED: 422,
  BULK_SUPPRESSED: 409,
  // 運営が止めている状態。契約者から見れば「今は使えない」なので 503。
  BULK_KILL_SWITCH_ENGAGED: 503,
  CAMPAIGN_RELEASE_REQUIRED: 409,
  SIGNUP_FORM_NOT_PUBLISHED: 409,
  OPTIN_EVIDENCE_REQUIRED: 422,
  BREAK_GLASS_REQUIRED: 403,

  CONSENT_REQUIRED: 403,
  CONSENT_VERSION_STALE: 409,
  // 法定ページが整うまでは「今は使えない」状態なので 503。
  SIGNUP_UNAVAILABLE: 503,
  // 形式は正しいが受け付けられないアドレスなので 422。
  SIGNUP_EMAIL_REJECTED: 422,

  UPSTREAM_ERROR: 502,
  SERVICE_UNAVAILABLE: 503,
  INTERNAL_ERROR: 500,
  NOT_IMPLEMENTED: 501,
};

/** コード → 既定の日本語メッセージ。呼び出し側で文脈に応じて差し替えてよい。 */
export const ERROR_MESSAGE_JA: Readonly<Record<ErrorCode, string>> = {
  VALIDATION_ERROR: '入力内容に誤りがあります',
  UNAUTHORIZED: '認証が必要です',
  FORBIDDEN: 'この操作を行う権限がありません',
  NOT_FOUND: '対象が見つかりません',
  CONFLICT: '既に同じ値が登録されています',
  REVISION_CONFLICT: '他の操作と競合しました。最新の内容を読み込んでやり直してください',
  RATE_LIMITED: 'リクエストが多すぎます。しばらく待って再度お試しください',
  PAYLOAD_TOO_LARGE: '送信データが大きすぎます',

  MFA_TICKET_INVALID: '認証セッションの有効期限が切れました。もう一度ログインしてください',
  MFA_REQUIRED: '二要素認証が必要です',
  REAUTH_REQUIRED: 'この操作にはパスワードの再入力が必要です',
  SESSION_REVOKED: 'セッションが失効しました。もう一度ログインしてください',
  ACCOUNT_LOCKED: 'アカウントが一時的にロックされています',

  PLAN_LIMIT_EXCEEDED: '現在のプランの上限に達しています',
  DNS_NOT_VERIFIED: 'DNS の設定が確認できていないため、この操作は実行できません',
  DOMAIN_ALREADY_CLAIMED: 'このドメインは既に登録されています',
  QUOTA_EXCEEDED: '容量の上限を超えています',
  ORGANIZATION_SUSPENDED: '契約が停止中のため、この操作は実行できません',
  BILLING_REQUIRED: 'お支払い情報の登録が必要です',

  NODE_UNREACHABLE: 'サーバーノードと通信できませんでした',
  AGENT_SIGNATURE_INVALID: '署名の検証に失敗しました',
  ENROLLMENT_TOKEN_INVALID: '登録トークンが無効です',
  AGENT_ASSERTION_REPLAYED: '認証アサーションが再利用されました',
  GENERATION_ROLLBACK: '設定世代が巻き戻っています',

  CONTENT_REVISION_CONFLICT:
    '他の担当者が同じ内容を編集しました。相手の版を開くか、自分の版を新しい下書きとして保存してください',
  CONTENT_LOCKED: '他の担当者が編集中です',
  CONTENT_NOT_APPROVED: 'この内容はまだ承認されていません',
  CONTENT_TOO_LARGE: '本文が大きすぎます。Gmail で末尾が省略され、法定表記が受信者から見えなくなります',
  CONTENT_LEGAL_FOOTER_MISSING: '法定の表示項目（送信者名・住所・配信停止先・問い合わせ先）が不足しています',
  CONTENT_MERGE_KEY_UNKNOWN: '宛先リストで定義されていない差し込み項目が使われています',
  FORM_CHALLENGE_REQUIRED: '確認のため追加の認証が必要です',
  FORM_RATE_LIMITED: '送信が多すぎます。しばらく待って再度お試しください',

  INTEGRATION_TOKEN_REQUIRED: 'この API は API トークン（Bearer mst_...）でのみ利用できます',
  IDEMPOTENCY_KEY_REUSED: '同じ Idempotency-Key で異なる内容が送信されました',
  CONTENT_HASH_MISMATCH: 'アップロードされた内容が申告されたハッシュと一致しません',
  CONTENT_UPLOAD_PENDING: 'この版はまだアップロードが完了していません',
  ASSET_IMMUTABLE: 'この画像は差し替えできません（差し替え可能として登録されていません）',
  TAG_MERGE_TARGET_INVALID: 'タグの統合先が不正です',
  WEBHOOK_URL_BLOCKED: 'この URL へは送信できません（内部ネットワーク宛、または https ではありません）',
  WEBHOOK_PII_ACK_REQUIRED: '受信者の個人情報を含むイベントの購読には、取り扱いへの同意が必要です',

  BULK_NOT_VERIFIED: '送信ストリームの検証が完了していません。DNS 設定と配信停止用アドレスの疎通をご確認ください',
  BULK_COMPLIANCE_BLOCKED: '法令上必要な表示または同意の記録が不足しているため配信できません',
  BULK_SUPPRESSED: 'この宛先は配信停止リストに登録されています',
  BULK_KILL_SWITCH_ENGAGED: '現在、一斉配信を一時停止しています。復旧までお待ちください',
  CAMPAIGN_RELEASE_REQUIRED: '配信の解禁が必要です。管理画面から解禁の操作を行ってください',
  SIGNUP_FORM_NOT_PUBLISHED: 'この申込フォームは現在受け付けていません',
  OPTIN_EVIDENCE_REQUIRED: '同意の取得方法と取得日時の記録が必要です',
  BREAK_GLASS_REQUIRED: 'この内容の閲覧には、別の管理者による承認と理由の記録が必要です',

  CONSENT_REQUIRED: '利用規約とプライバシーポリシーへの同意が必要です',
  CONSENT_VERSION_STALE:
    '表示中に規約が改定されました。最新の内容をご確認のうえ、あらためて同意してください',
  SIGNUP_UNAVAILABLE: '現在、新規のお申し込みを受け付けていません',
  SIGNUP_EMAIL_REJECTED:
    'このメールアドレスではお申し込みいただけません。継続して受信できるアドレスをご登録ください',

  UPSTREAM_ERROR: '外部サービスと通信できませんでした',
  SERVICE_UNAVAILABLE: '現在この機能を利用できません。しばらく待って再度お試しください',
  INTERNAL_ERROR: 'サーバー内部でエラーが発生しました',
  NOT_IMPLEMENTED: 'この機能はまだ実装されていません',
};

/* -------------------------------------------------------------- 封筒の型 */

export const errorEnvelopeSchema = z.object({
  error: z.object({
    code: z.string().regex(/^[A-Z][A-Z0-9_]*$/, 'エラーコードは SNAKE_UPPER で表します'),
    message: z.string().min(1),
    details: z.unknown().optional(),
  }),
});

export interface ErrorEnvelope {
  error: {
    code: string;
    message: string;
    details?: unknown;
  };
}

/** 封筒を組み立てる。`message` 省略時は既定の日本語を使う。 */
export function errorEnvelope(code: ErrorCode, message?: string, details?: unknown): ErrorEnvelope {
  return {
    error: {
      code,
      message: message ?? ERROR_MESSAGE_JA[code],
      ...(details === undefined ? {} : { details }),
    },
  };
}

/** 未知の応答から封筒を安全に取り出す。SPA のエラー処理はこれを通す。 */
export function parseErrorEnvelope(body: unknown): ErrorEnvelope['error'] | null {
  const parsed = errorEnvelopeSchema.safeParse(body);
  return parsed.success ? parsed.data.error : null;
}

/** `PLAN_LIMIT_EXCEEDED` の details（§4.3）。SPA はここからアップグレード導線を出す。 */
export const planLimitDetailsSchema = z.object({
  /** 制限の識別子（例: `mailboxesMax`） */
  limit: z.string(),
  current: z.number().int().nonnegative(),
  max: z.number().int().nonnegative(),
  /** 対象リソース（例: `mailbox` / `domain` / `alias`） */
  resource: z.string(),
});
export type PlanLimitDetails = z.infer<typeof planLimitDetailsSchema>;
